import React, { useMemo } from 'react';
import * as d3 from 'd3';

const DonutChart = ({ 
  data, 
  settings, 
  centerText,
  id,
  sortedPieColorObject,
  pieType
}) => {
  const {
    width = 450,
    height = 400,
    innerRadius = 40,
    outerRadius = 80,
    color = [],
    textSize = '11px',
    legendColorMap = {},
    maxLine1Length = 105,
    show3rdLine = false // New configurable key
  } = settings;
  
  const containerWidth = document.getElementById(id)?.offsetWidth;
  const isSmallContainer = (containerWidth < 1000) || false;

  const getFontSize = () => {
    if (containerWidth < 900) return '11px';
    if (containerWidth < 1000) return '12px';
    return '14px';
  };

  const relaxLabels = (sideLabels) => {
    const minGap = 5; 
    const iterations = 500;
    const lineHeight = 14; 

    for (let i = 0; i < iterations; i++) {
      let collisions = 0;
      for (let j = 0; j < sideLabels.length - 1; j++) {
        let a = sideLabels[j];
        let b = sideLabels[j + 1];

        const aHalfHeight = (a.lines.length * lineHeight) / 2;
        const bHalfHeight = (b.lines.length * lineHeight) / 2;

        const aBottom = a.posC[1] + aHalfHeight;
        const bTop = b.posC[1] - bHalfHeight;

        if (aBottom + minGap > bTop) {
          collisions++;
          const overlap = (aBottom + minGap) - bTop;
          
          a.posC[1] -= overlap * 0.5;
          b.posC[1] += overlap * 0.5;
          
          a.posB[1] = a.posC[1];
          b.posB[1] = b.posC[1];
        }
      }
      if (collisions === 0) break;
    }
  };

  const { 
    pieData, 
    labels, 
    colorScale, 
    progressAngle, 
    startAngle, 
    endAngle,
    othersConnectingPath,
    gradientId 
  } = useMemo(() => {
    const colorS = d3.scaleOrdinal(color.length > 0 ? color : d3.schemeCategory10);
    const gradId = `others-gradient-${Math.random().toString(36).substr(2, 9)}`;
    
    const sortedData = [...data].sort((a, b) => {
      const special = ['OTHERS', 'REMAINING_VALUE'];
      if (special.includes(a.name) || special.includes(b.name)) {
        if (a.name === 'OTHERS') return 1;
        if (b.name === 'OTHERS') return -1;
        if (a.name === 'REMAINING_VALUE') return 1;
        if (b.name === 'REMAINING_VALUE') return -1;
      }
      return b.value - a.value;
    });

    const sAngle = (3 * Math.PI) / 4; 
    const eAngle = (2 * Math.PI) + sAngle;

    const pieGenerator = d3.pie()
      .value(d => d.value)
      .sort(null)
      .startAngle(sAngle)
      .endAngle(eAngle)
      .padAngle(0.01);

    const calculatedPieData = pieGenerator(sortedData);
    const pctValue = parseFloat(centerText) || 0;
    const pAngle = sAngle + (pctValue / 100) * (2 * Math.PI);

    let foundOthersPath = null;
    
    const hMargin = 20; 
    const poleBound = outerRadius * 1.5;
    const equatorBound = (width / 2) - hMargin;

    const labelBounds = d3.scaleLinear()
      .domain([0, height / 2])
      .range([equatorBound, poleBound]);

    const processedLabels = calculatedPieData
      .filter(d => d.data.value > 0 && d.data.name !== 'REMAINING_VALUE')
      .map((d, i) => {
        const name = d.data.name || "";
        const isOthers = name === 'OTHERS' || name === 'OTHER';

        if (isOthers && !foundOthersPath) {
          const sA = d.startAngle - Math.PI / 2;
          const eA = d.endAngle - Math.PI / 2;
          const halfR = 95;
          const attX = width / 2 - 15;
          const cPath = d3.path();
          cPath.moveTo(Math.cos(sA) * outerRadius, Math.sin(sA) * outerRadius);
          cPath.lineTo(attX, -halfR);
          cPath.bezierCurveTo(attX + 60, -halfR, attX + 120, halfR, attX, halfR);
          cPath.lineTo(Math.cos(eA) * outerRadius, Math.sin(eA) * outerRadius);
          cPath.arc(0, 0, outerRadius, eA, sA, true);
          cPath.closePath();
          foundOthersPath = cPath.toString();
        }

        const angle = (d.startAngle + d.endAngle) / 2 - Math.PI / 2;
        const isRight = Math.cos(angle) >= 0;
        const posA = [Math.cos(angle) * outerRadius, Math.sin(angle) * outerRadius];
        const elbowDist = outerRadius + 15;
        const posB = [Math.cos(angle) * elbowDist, Math.sin(angle) * elbowDist];

        const absY = Math.abs(Math.sin(angle) * (outerRadius * 1.3));
        const currentXBound = labelBounds(absY);
        let desiredShelfX = (currentXBound - 40) * (isRight ? 1 : -1);

        let horizontalDist = Math.abs(desiredShelfX - posB[0]);
        const clampedDist = Math.max(20, Math.min(horizontalDist, maxLine1Length));
        const shelfX = posB[0] + (isRight ? clampedDist : -clampedDist);

        const totalChars = name.length;
        let charLimit;

        // Dynamic char limit calculation
        if (isSmallContainer) {
          charLimit = Math.max(10, Math.ceil(totalChars / (show3rdLine ? 3.5 : 2.5)));
        } else {
          charLimit = Math.max(20, Math.ceil(totalChars / (show3rdLine ? 3 : 2)));
        }
        
        const words = name.split(" ");
        let lines = [];
        let tempLine = "";

        words.forEach(word => {
          if ((tempLine + word).length > charLimit && tempLine !== "") {
            lines.push(tempLine.trim());
            tempLine = word + " ";
          } else {
            tempLine += word + " ";
          }
        });
        lines.push(tempLine.trim());

        // Handle Max Wrapping Lines based on show3rdLine
        const maxLines = show3rdLine ? 3 : 2;
        if (lines.length > maxLines) {
          const mainLines = lines.slice(0, maxLines - 1);
          const lastLineText = lines.slice(maxLines - 1).join(" ");
          lines = [...mainLines, lastLineText];
        }

        return { 
          data: d, index: i, posA, posB, posC: [shelfX, posB[1]], 
          isRight, lines, textHeight: lines.length * 14
        };
      });

    relaxLabels(processedLabels.filter(l => l.isRight).sort((a, b) => a.posC[1] - b.posC[1]));
    relaxLabels(processedLabels.filter(l => !l.isRight).sort((a, b) => a.posC[1] - b.posC[1]));

    return { 
      pieData: calculatedPieData, labels: processedLabels, colorScale: colorS, 
      progressAngle: pAngle, startAngle: sAngle, endAngle: eAngle,
      othersConnectingPath: foundOthersPath, gradientId: gradId
    };
  }, [data, color, outerRadius, centerText, width, height, isSmallContainer, maxLine1Length, show3rdLine]);

  const arcGenerator = d3.arc().innerRadius(innerRadius).outerRadius(outerRadius);
  const centerProgressArc = d3.arc().innerRadius(innerRadius - 6).outerRadius(innerRadius - 2);

  const getFillColor = (d, i) => {
 
    if (d.data.name === 'OTHERS' || d.data.name === 'OTHER') return '#64948E'; 
    if (d.data.name === 'REMAINING_VALUE') return '#f1f1f1';

    if(sortedPieColorObject?.[d.data.name]) return sortedPieColorObject[d.data.name];
    
    if (legendColorMap[d.data.name]) return legendColorMap[d.data.name];
    // if (d.data.name === 'OTHERS' || d.data.name === 'OTHER') return colorScale(i);
    return colorScale(i);
  };

  return React.createElement('svg', { 
    id: id,
    width: "100%", height: height, viewBox: `0 0 ${width} ${height}`, style: { overflow: 'visible' } 
  }, 
    React.createElement('defs', {},
      React.createElement('linearGradient', { id: gradientId, x1: '0%', y1: '0%', x2: '100%', y2: '0%' },
        React.createElement('stop', { offset: '0%', stopColor: '#DCE0E3', stopOpacity: 1 }),
        React.createElement('stop', { offset: '100%', stopColor: '#ffffff', stopOpacity: 0.1 })
      )
    ),

    React.createElement('g', { transform: `translate(${width / 2}, ${height / 2})` },
      
      othersConnectingPath && React.createElement('path', {
        d: othersConnectingPath, fill: `url(#${gradientId})`, stroke: 'none'
      }),

      pieData.map((d, i) => 
        React.createElement('path', {
          key: i, d: arcGenerator(d), fill: getFillColor(d, i), stroke: "#fff", strokeWidth: "1"
        })
      ),

      React.createElement('path', { d: centerProgressArc({ startAngle: progressAngle, endAngle: endAngle }), fill: "#DCE0E3" }),
      React.createElement('path', { d: centerProgressArc({ startAngle: startAngle, endAngle: progressAngle }), fill: "#000" }),

      React.createElement('text', { textAnchor: "middle", dy: "0.35em", fontWeight: "700", fontSize: "14px", fill: "#333" }, centerText),

      labels.map((l, i) => {
        const isOthers = l.data.data.name === "OTHERS" || l.data.data.name === "OTHER";
        
        // Precise Vertical Centering Logic
        const totalLines = l.lines.length;
        const lineSpacing = 1.1; 
        // We calculate dy based on total block height to ensure posC[1] is exactly in the middle
        const dyOffset = 0.35 - ((totalLines - 1) * (lineSpacing / 2));

        return React.createElement('g', { 
          key: i, style: { opacity: isOthers ? 0 : 1, pointerEvents: isOthers ? 'none' : 'auto' } 
        },
          React.createElement('polyline', {
            points: `${l.posA} ${l.posB} ${l.posC}`,
            fill: "none", stroke: "#ccc", strokeWidth: "1"
          }),
          React.createElement('circle', { cx: l.posA[0], cy: l.posA[1], r: 3, fill: getFillColor(l.data, l.index), stroke: "#fff" }),
          React.createElement('text', {
            x: l.posC[0] + (l.isRight ? 6 : -6),
            y: l.posC[1],
            textAnchor: l.isRight ? 'start' : 'end',
            fontSize: isSmallContainer ? getFontSize() : textSize,
            fill: "#444", 
            dy: `${dyOffset}em` 
          }, 
            l.lines.map((line, idx) => 
              React.createElement('tspan', {
                key: idx, 
                x: l.posC[0] + (l.isRight ? 6 : -6), 
                dy: idx > 0 ? `${lineSpacing}em` : '0em'
              }, 
                l.isRight 
                  ? [line, idx === 0 ? " " : "", idx === 0 ? React.createElement('tspan', { key: 'v', fontWeight: "bold", fill: getFillColor(l.data, l.index) }, `${l.data.data.value.toFixed(0)}%`) : null]
                  : [idx === 0 ? React.createElement('tspan', { key: 'v', fontWeight: "bold", fill: getFillColor(l.data, l.index) }, `${l.data.data.value.toFixed(0)}%`) : null, idx === 0 ? " " : "", line]
              )
            )
          )
        );
      })
    )
  );
};

export default DonutChart;